import HomeHelmet from 'components/home/HomeHelmet';
import { array } from 'prop-types';
import { useLocaleContext } from 'providers/LocaleProvider';
import React from 'react';
import styled from 'styled-components';
import { Title } from '@seaweb/coral/components/Typography';
import Layout from '@seaweb/coral/components/Layout';
import HomeSearchField from 'components/home/HomeSearchField';
import CategoryBox from 'components/categories/CategoryBox';
import ArticleBox from 'components/categories/ArticleBox';
import { useViewContext } from 'providers/ViewContextProvider';
import ContactSection from 'components/contact/ContactSection';
import { serverFetch } from 'utils/serverFetch';
import HomeBackground from 'components/home/HomeBackground.svg';

const SearchLayout = styled(Layout)`
  height: 316px;
  display: flex;
  justify-content: center;
  position: relative;
  background-color: #2091f1;
  margin: 0 auto;

  > svg {
    position: absolute;
    width: 2560px;
    height: 316px;
    z-index: 0;
    margin: auto;
    left: 50%;
    transform: translate(-50%, 0);
  }

  @media screen and (max-width: 600px) {
    height: 180px;

    > svg {
      height: 180px;
    }
  }
`;

const SearchTitle = styled(Title)`
  font-size: 38px;
  font-weight: bold;
  color: white;
  margin: 0;
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;
  z-index: 1;

  @media screen and (max-width: 600px) {
    font-size: 24px;
  }
  > * {
    margin-right: 8px;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const CategorySection = styled.div`
  background-image: url('/images/st-pic.svg'), url('/images/st-message.svg');
  background-position: left 50px center, top 50px right 30px;
  background-repeat: no-repeat, no-repeat;
  padding: 60px 20px;
  @media screen and (max-width: 600px) {
    background-image: none;
    padding: 20px 20px 40px;
  }
`;

const CategoryContent = styled.div`
  margin: auto;
  max-width: 1160px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const FeaturedSection = styled.div`
  background-color: #f9fbfe;
  background-image: url('/images/st-safe.svg'), url('/images/st-chat.svg'),
    url('/images/st-phone.svg');
  background-position: left 50px center, top 50px right 30px,
    bottom 50px right 30px;
  background-repeat: no-repeat, no-repeat, no-repeat;
  padding: 60px 20px;
  @media screen and (max-width: 600px) {
    background-image: none;
    padding: 20px 20px 40px;
  }
`;

const FeaturedContent = styled.div`
  margin: auto;
  max-width: 1160px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const FeaturedTitle = styled(Title)`
  font-size: 32px;
  font-weight: bold;
  color: #202325;
  margin: 0 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  @media screen and (max-width: 600px) {
    font-size: 20px;
    margin: 20px 0;
  }
`;

function HomePage({ categories, featuredArticles }) {
  const { locale: t } = useLocaleContext();
  const { isMobileView } = useViewContext();

  return (
    <div style={{ overflowX: 'hidden' }}>
      <HomeHelmet />

      <SearchLayout>
        <HomeBackground />

        <SearchTitle> {t('hc.search.header_title')} </SearchTitle>

        <Row>
          <HomeSearchField style={{ height: isMobileView ? 42 : 48 }} />
        </Row>
      </SearchLayout>

      <CategorySection>
        <CategoryContent>
          {categories.map((category) => (
            <CategoryBox
              img={category.logoSrc}
              key={category.id}
              name={category.name}
              description={category.description}
              articleCount={category.articleCount}
              href={`/categories/${category.id}`}
            />
          ))}
        </CategoryContent>
      </CategorySection>

      <FeaturedSection>
        <FeaturedTitle>{t('hc.homepage.featured_articles')}</FeaturedTitle>
        <FeaturedContent>
          {featuredArticles.map((article) => (
            <ArticleBox
              href={`/categories/${article.category.id}/article/${article.id}`}
              key={article.id}
              title={article.title}
              preview={article.preview}
            />
          ))}
        </FeaturedContent>
      </FeaturedSection>

      {isMobileView && <ContactSection />}
    </div>
  );
}

HomePage.propTypes = {
  categories: array,
  featuredArticles: array,
};

export async function getServerSideProps(context) {
  try {
    const [categoriesResponse, featureArticlesResponse] = await Promise.all([
      serverFetch(context, `/categories/`, 'GET'),
      serverFetch(context, `/featured-articles/`, 'GET'),
    ]);

    return {
      props: {
        categories: categoriesResponse.data.categories,
        featuredArticles: featureArticlesResponse.data.articles,
      },
    };
  } catch (e) {
    return { notFound: true };
  }
}

export default HomePage;
